import { useState } from "react";
import { CircleCheck, CircleX, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ChoiceExercise } from "./ChoiceExercise";
import { TextExercise } from "./TextExercise";
import { MatchExercise } from "./MatchExercise";
import { OrderExercise } from "./OrderExercise";
import { SpeakExercise } from "./SpeakExercise";

const LABELS = {
  mcq: "Choose the right answer",
  listen: "Listen and choose",
  fill_blank: "Fill in the blank",
  match: "Match the pairs",
  order: "Put the words in order",
  translate: "Translate",
  speak: "Say it aloud",
};

const COMPONENTS = { mcq: ChoiceExercise, listen: ChoiceExercise, fill_blank: TextExercise, translate: TextExercise, match: MatchExercise, order: OrderExercise, speak: SpeakExercise };

export const ExerciseRunner = ({ exercises, lang, onFinish, onExit, onResult }) => {
  const list = exercises.filter((e) => COMPONENTS[e.type]);
  const [idx, setIdx] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [correct, setCorrect] = useState(0);
  const ex = list[idx];
  const Comp = COMPONENTS[ex.type];

  const submit = (ok, answer, note) => {
    if (ok === null) return advance(correct);
    setFeedback({ ok, answer, note });
    if (ok) setCorrect((c) => c + 1);
    onResult?.(ex, ok);
  };
  const advance = (c) => {
    setFeedback(null);
    if (idx + 1 >= list.length) onFinish(c, list.length);
    else setIdx(idx + 1);
  };
  const next = () => advance(correct);

  return (
    <div className="max-w-2xl mx-auto pb-44">
      <div className="flex items-center gap-4 mb-8">
        <button data-testid="exercise-exit-button" onClick={onExit} className="text-slate-400 hover:text-slate-700"><X /></button>
        <Progress data-testid="exercise-progress" value={((idx + (feedback ? 1 : 0)) / list.length) * 100} className="h-3 flex-1" />
        <span className="text-sm font-bold text-slate-500">{idx + 1}/{list.length}</span>
      </div>
      <div key={idx} className="animate-fade-up" data-testid={`exercise-${ex.type}`}>
        <p className="caption mb-2">{LABELS[ex.type]}</p>
        {["mcq", "listen", "match", "speak"].includes(ex.type) && (
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">{ex.prompt}</h2>
        )}
        {ex.type === "match" && <p className="text-sm text-slate-500 -mt-4 mb-5">Tap a word on the left, then its meaning on the right.</p>}
        <Comp ex={ex} lang={lang} locked={!!feedback} onSubmit={submit} />
      </div>
      {feedback && (
        <div data-testid="exercise-feedback" className={`fixed bottom-0 inset-x-0 z-30 border-t-2 animate-slide-up ${feedback.ok ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]" : "bg-[hsl(var(--error-bg))] border-[hsl(var(--error))]"}`}>
          <div className="max-w-2xl mx-auto px-5 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 flex gap-3">
              {feedback.ok ? <CircleCheck className="text-[hsl(var(--success))] shrink-0" size={30} /> : <CircleX className="text-[hsl(var(--error))] shrink-0" size={30} />}
              <div>
                <p data-testid="exercise-feedback-title" className={`font-display font-extrabold text-lg ${feedback.ok ? "text-[hsl(var(--success-text))]" : "text-[hsl(var(--error-text))]"}`}>
                  {feedback.ok ? "Nice work!" : "Not quite"}
                </p>
                {!feedback.ok && !["match", "speak"].includes(ex.type) && <p className="text-sm text-slate-700">Correct answer: <b>{feedback.answer}</b></p>}
                {(feedback.note || ex.explanation) && <p className="text-sm text-slate-600 mt-0.5">{feedback.note || ex.explanation}</p>}
              </div>
            </div>
            <button data-testid="exercise-continue-button" onClick={next} className={`btn-primary sm:w-40 ${feedback.ok ? "" : "!bg-[hsl(var(--error))]"}`}>
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
